import React, { useEffect, useMemo, useRef, useState } from "react";
import ExpenseForm, { ExpenseFormValues } from "./expense-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Account, ApiResponse, Expense } from "@/lib/types";
import { Step } from "./expense-form/constants";
import { useGetVendors } from "@/services/queries/vendor";
import { api, BASE_URL } from "@/lib/api";
import { toDateOnlyString } from "@/lib/utils";
import {
  useCreateTransactionAttachment,
  useDeleteTransactionAttachment,
  useUpdateExpense,
} from "@/services/mutations/transactions";
import { toast } from "@/hooks/use-toast";
import { useCreateVendor } from "@/services/mutations/vendor";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { z } from "zod";
import { useGetAccounts } from "@/services/queries/accounts";
import { useConversionRate } from "@/hooks/use-conversion-rate";

const EditExpenseForm = ({
  open,
  setOpen,
  expense,
  refetch,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  expense: Expense;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);
  const [step, setStep] = useState<number>(Step.INITAL);
  const { data: vendorsData } = useGetVendors();
  const { data: accountsData } = useGetAccounts();
  const { mutateAsync: createTransactionAttachment } =
    useCreateTransactionAttachment();
  const { mutateAsync: deleteTransactionAttachment } =
    useDeleteTransactionAttachment();
  const { mutate: createVendor, isPending: isCreatingVendor } = useCreateVendor(
    {
      onSuccess: ({ value }) => {
        handleUpdateExpense(form.getValues(), value.id);
        Mixpanel.track("Vendor Added When Editing Expense Transaction");
        // Keep isSubmittingRef true — updateExpense's own handlers clear it.
      },
      onError: () => {
        Mixpanel.track(
          "Vendor Addition Failed When Editing Expense Transaction"
        );
        isSubmittingRef.current = false;
      },
    }
  );
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const { mutate: updateExpense, isPending: isUpdatingExpense } =
    useUpdateExpense({
      onSuccess: async ({ data }) => {
        if (form.getValues("files")) {
          const deletedAttachments =
            "attachments" in expense
              ? expense.attachments.filter(
                  (attachment) =>
                    !form
                      .watch("files")
                      .some(
                        (file) => (file as { id: string }).id === attachment.id
                      )
                )
              : [];

          const newAttachments = form
            .watch("files")
            .filter((file) => file instanceof File);

          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // transaction was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled([
            ...newAttachments.map((file) =>
              createTransactionAttachment({
                file,
                transactionId: data.value.id,
              })
            ),
            ...(deletedAttachments ?? []).map((attachment) =>
              deleteTransactionAttachment({
                transactionId: data.value.id,
                attachmentId: attachment.id,
              })
            ),
          ]);
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
          refetch();
          setOpen(false);
          form.setValue("vendor", {
            id: "vendorId" in data.value ? data.value.vendorId : "",
          });
          queryClient.invalidateQueries({
            queryKey: ["vendors"],
          });
          form.reset();
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_expense_description"),
          });

          Mixpanel.track("Expense Edited Successfully");
        } else {
          refetch();
          setOpen(false);
          form.setValue("vendor", {
            id: "vendorId" in data.value ? data.value.vendorId : "",
          });
          queryClient.invalidateQueries({
            queryKey: ["vendors"],
          });
          form.reset();
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_expense_description"),
          });

          Mixpanel.track("Expense Edited Successfully");
        }
        isSubmittingRef.current = false;
      },
      onError: () => {
        Mixpanel.track("Expense Edit Failed");
        isSubmittingRef.current = false;
      },
    });

  const accounts = useMemo(() => {
    return accountsData?.value ?? [];
  }, [accountsData?.value]);

  const vendors = useMemo(() => vendorsData?.value ?? [], [vendorsData]);

  const defaultVendor = useMemo(() => {
    return vendors.find(
      (vendor) => "vendorId" in expense && vendor.id === expense.vendorId
    );
  }, [vendors, expense]);

  const defaultValues: Partial<ExpenseFormValues> = useMemo(() => {
    return {
      ...expense,
      transactionDate: expense?.transactionDate
        ? new Date(expense.transactionDate)
        : undefined,
      files:
        ("attachments" in expense ? (expense.attachments ?? []) : []).map(
          (attachment) => {
            return { id: attachment.id, url: attachment.filePath };
          }
        ) || [],
      vendor: defaultVendor ? { id: defaultVendor.id } : null,
      accountId: "fromAccountId" in expense ? expense.fromAccountId : "",
      amount: Math.abs(expense.amount),
      exchangeRate: expense.ledgerEntries?.find(
        (entry) => entry.accountId === expense.fromAccountId
      )?.conversionRate,
      vendorPaymentMethods: [],
    };
  }, [expense, defaultVendor]);

  const expenseFormSchema = z.object({
    amount: z.number().min(1),
    currencyId: z.string().min(1),
    reference: z.string().optional(),
    budgetId: z.string().min(1),
    budgetCategoryId: z.string().optional(),
    budgetSubcategoryId: z.string().optional(),
    vendor: z
      .string()
      .or(z.object({ id: z.string().min(1) }))
      .or(z.null())
      .refine((value) => {
        if (value && typeof value === "object" && "id" in value) {
          return value.id.length > 0;
        } else if (value && typeof value === "string") {
          return value.length > 0;
        }
        return false;
      }), // Vendor can be a string (name), object (with id), or null
    vendorPaymentMethods: z.array(
      z.object({
        iban: z.string().optional(),
        bankName: z.string().optional(),
        swiftCode: z.string().optional(),
        accountHolderName: z.string().optional(),
        currencyId: z.string().optional(),
        bankAddress: z.string().optional(),
        bankAccountNumber: z.string().optional(),
        paymentTerms: z.string().optional(),
        isDefault: z.boolean().optional(),
      })
    ),
    accountId: z.string().min(1),
    transactionDate: z.date(),
    files: z.array(z.any()).optional(),
    description: z.string().optional(),
    vesselId: z.string().min(1),
    exchangeRate: z
      .number()
      .or(z.undefined())
      .refine((val) => {
        if (selectedAccount?.currencyId === form.getValues("currencyId")) {
          return true;
        }

        return val !== undefined;
      }),
  });

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues,
  });

  // Auto-resolve the conversion rate at the transaction date so backdated
  // edits use the historically correct rate (backend falls back to closest).
  const watchedCurrencyId = form.watch("currencyId");
  const watchedTransactionDate = form.watch("transactionDate");
  const transactionDateStr = watchedTransactionDate
    ? toDateOnlyString(watchedTransactionDate)
    : undefined;
  const { rate: exchangeRate, setRate: setExchangeRate } = useConversionRate({
    fromCurrencyId: watchedCurrencyId,
    toCurrencyId: selectedAccount?.currencyId,
    date: transactionDateStr,
    initialRate: expense.ledgerEntries?.find(
      (entry) => entry.accountId === expense.fromAccountId
    )?.conversionRate,
  });

  useEffect(() => {
    form.setValue("exchangeRate", exchangeRate);
  }, [exchangeRate, form]);

  const handleUpdateExpense = (values: ExpenseFormValues, vendorId: string) => {
    updateExpense({
      id: expense.id,
      version: "version" in expense && expense.version ? expense.version : 0,
      transactionDate: values.transactionDate
        ? toDateOnlyString(values.transactionDate)
        : "",
      amount: values.amount ?? 0,
      description: values.description,
      reference: values.reference ?? "",
      currencyId: values.currencyId,
      accountId: values.accountId ?? "",
      vesselId: values.vesselId,
      vendorId: vendorId,
      budgetSubcategoryId: values.budgetSubcategoryId,
      accountConversionRate: values.exchangeRate,
    });
  };

  const onSubmit = (values: ExpenseFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (typeof values.vendor === "object" && values.vendor !== null) {
      handleUpdateExpense(values, values.vendor.id);
    } else if (typeof values.vendor === "string") {
      const existingVendor = vendors?.find(
        (vendor) =>
          vendor.name.toLocaleLowerCase() ===
          (values.vendor as string).toLocaleLowerCase()
      );

      if (existingVendor) {
        handleUpdateExpense(values, existingVendor.id);
      } else {
        createVendor({
          name: values.vendor,
          paymentMethods: (values.vendorPaymentMethods ?? []).map((item) => {
            return {
              ...item,
              name: values.vendor as string,
            };
          }),
        });
      }
    }
  };

  useEffect(() => {
    const getPreviewUrls = async () => {
      const files = "attachments" in expense ? (expense.attachments ?? []) : [];

      if (!files || files.length === 0) {
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `${BASE_URL}/transactions/attachments/${file.id}/url`
            );
            return { id: file.id, url: res.data.value };
          })
        );
        form.setValue("files", urlResults);
      } catch (error) {
        console.error("Error fetching attachment URLs:", error);
      }
    };

    getPreviewUrls();
  }, [expense, form, open]);

  useEffect(() => {
    // Fix for the vendor not being set when the form is opened
    if (
      vendors.length > 0 &&
      "vendorId" in expense &&
      expense.vendorId !== undefined
    ) {
      form.setValue("vendor", { id: expense.vendorId });
    }
  }, [vendors, expense, form]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "accountId") {
        const account = accounts.find(
          (account) => account.id === value.accountId
        );
        setSelectedAccount(account ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, [accounts, form]);

  useEffect(() => {
    if (accounts.length > 0 && expense.fromAccountId) {
      const account = accounts.find(
        (account) => account.id === expense.fromAccountId
      );
      setSelectedAccount(account ?? null);
    }
  }, [accounts, expense.fromAccountId]);

  return (
    <>
      <ExpenseForm
        title={t("title.edit_expense")}
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        initialVesselId={undefined}
        selectedAccount={selectedAccount ?? undefined}
        exchangeRate={exchangeRate}
        setExchangeRate={setExchangeRate}
        isSubmitting={isUpdatingExpense || isCreatingVendor}
      />
    </>
  );
};

export default EditExpenseForm;
